import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { SITE, telLink } from "../lib/site";
import { EASE } from "./motion";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/industries", label: "Industries", id: "industries" },
  { to: "/insights", label: "Insights", id: "insights" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:bg-gold focus:px-4 focus:py-2 focus:text-navy"
      >
        Skip to content
      </a>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-navy/10 shadow-[0_1px_30px_-15px_rgba(11,21,40,0.35)]"
            : "bg-transparent"
        }`}
      >
        <div className="container-x flex h-20 items-center justify-between">
          <Link to="/" data-testid="nav-link-home" aria-label="Upgrade Law — home" className="shrink-0">
            <Logo light={!scrolled || open} />
          </Link>

          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-medium tracking-[0.08em] uppercase transition-colors duration-300 ${
                    scrolled ? "text-steel hover:text-navy" : "text-white/75 hover:text-white"
                  } ${isActive ? "!text-gold" : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-enquiry-cta-button"
              className={`btn !px-6 !py-3 text-[13px] ${
                scrolled
                  ? "bg-navy text-white hover:bg-navy-surface hover:shadow-[0_10px_30px_-12px_rgba(11,21,40,0.6)]"
                  : "bg-gold text-navy hover:bg-gold-light"
              }`}
            >
              Submit a Legal Enquiry
            </Link>
          </nav>

          <div className="flex items-center gap-4 lg:hidden">
            <a
              href={telLink}
              data-testid="floating-phone-call-button"
              aria-label={`Call ${SITE.phoneDisplay}`}
              className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                scrolled ? "border-navy/20 text-navy" : "border-white/25 text-white"
              }`}
            >
              <Phone size={16} />
            </a>
            <button
              data-testid="nav-mobile-menu-toggle"
              aria-expanded={open}
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                open ? "border-gold text-gold" : scrolled ? "border-navy/20 text-navy" : "border-white/25 text-white"
              }`}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-40 bg-navy blueprint-grid lg:hidden"
          >
            <div className="flex h-full flex-col justify-between pt-28 pb-10 container-x">
              <nav aria-label="Mobile" className="flex flex-col gap-1">
                {LINKS.map((l, i) => (
                  <motion.div
                    key={l.id}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`nav-link-${l.id}`}
                      className={({ isActive }) =>
                        `group flex items-baseline gap-4 border-b border-white/10 py-4 ${
                          isActive ? "text-gold" : "text-white"
                        }`
                      }
                    >
                      <span className="font-mono text-[10px] text-gold/70">0{i + 1}</span>
                      <span className="font-serif text-4xl">{l.label}</span>
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5, ease: EASE }}
                className="space-y-4"
              >
                <Link to="/contact" data-testid="nav-enquiry-cta-button" className="btn-gold w-full">
                  Submit a Legal Enquiry
                </Link>
                <p className="font-mono text-[10px] uppercase tracking-widest2 text-white/40">
                  {SITE.phoneDisplay} · {SITE.email}
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
